import { Dispatch, FocusEvent, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useMountedState } from 'react-use';
import debounce from 'lodash.debounce';
import { connect } from 'react-redux';

import { ILegalEntityLookup } from 'APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { isStringDigitsOnly } from 'CorePackage/MainModule/validators/isStringDigitsOnly';

import { generateLegalEntityLookupText } from '../common/generateLegalEntityLookupText';
import { useFnsLookupByInn } from '../hooks/useFnsLookupByInn';
import { ILegalEntityAutoCompleteFieldBasicProps, LegalEntityAutoCompleteField } from './LegalEntityAutoCompleteField';
import { ILegalEntityAutoCompleteOption } from '../interfaces/ILegalEntityAutoCompleteOption';
import { FnsCheckStatusesEnum } from '../../../KnmErknmPackage/ErknmSubjectModule/enums/FnsCheckStatusesEnum';
import { convertInitialKnmDateToFormDate } from '../common/convertInitialKnmDateToFormDate';
import { DATE_FORMAT_LONG_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import { KnmSubjectTypesEnum } from '../../../KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ITargetEntity } from '../../../APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { useFnsRegistrySearchInn } from '../hooks/useFnsRegistrySearch';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getKnmDataCreated } from '../selectors/knmSelectors';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IKnmCheckSubjectResult } from '../../../APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { FnsRegistryCheckMessageEnum } from '../enums/FnsRegistryCheckMessageEnum';

type IKnmInnFieldProps = ILegalEntityAutoCompleteFieldBasicProps & {
  checkSubjectResult?: IKnmCheckSubjectResult | null;
  created: string | null | undefined;
  ervkId?: string;
  inn?: string;
  isErp?: boolean;
  isFiz?: boolean;
  isFnsCheckFound?: boolean;
  isShowAutoCompleteField?: boolean;
  onAutoCompleteChange?: (value: string) => void;
  onAutoCompleteSelect?: (legalEntityLookup: ILegalEntityLookup) => void;
  onChangeInn?: (value: string) => void;
  onLoadingChange?: (value: boolean) => void;
  setCheckByInnMessage?: Dispatch<SetStateAction<string | undefined>>;
  setLookupData?: (data: ILegalEntityLookup | boolean | undefined) => void;
  settings: IAdminSetting[] | undefined;
  subjectType?: IDictionarySelectOption<ITargetEntity> | null;
  isShowAdditionalMessages?: boolean;
};

export const KnmInnFieldWithoutProps = ({
  checkSubjectResult,
  created,
  ervkId,
  inn,
  isErp,
  isFiz,
  isFnsCheckFound,
  isShowAutoCompleteField,
  onAutoCompleteChange,
  onAutoCompleteSelect,
  onChangeInn,
  onLoadingChange,
  setCheckByInnMessage,
  setLookupData,
  settings,
  subjectType,
  isShowAdditionalMessages,
  ...rest
}: IKnmInnFieldProps) => {
  const isMounted = useMountedState();
  const { isLoading: isFnsLookupByInnLoading, getFnsLookupByInn } = useFnsLookupByInn();
  const { getFnsRegistryDateInn } = useFnsRegistrySearchInn();
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<ILegalEntityAutoCompleteOption[] | undefined>();
  const [fnsCheckDateMessage, setFnsCheckDateMessage] = useState<string | undefined>(undefined);
  const [fnsRegistryCheckDateMessage, setFnsRegistryCheckDateMessage] = useState<string | undefined>(undefined);
  const [fnsRegistryErrorMessage, setFnsRegistryErrorMessage] = useState<string | undefined>(undefined);

  const isShowCheckResultMessage = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.SHOW_CHECK_RESULT_MESSAGE_523_4_1_17,
  );

  useEffect(() => {
    onLoadingChange && onLoadingChange(isFnsLookupByInnLoading);
  }, [isFnsLookupByInnLoading, onLoadingChange]);

  const fetchFnsRegistry = useCallback(
    async (innValue: string) => {
      const getFnsRegistryDateByInnResponse = await getFnsRegistryDateInn(innValue);

      setFnsRegistryErrorMessage(undefined);

      if (!isMounted()) {
        setFnsRegistryCheckDateMessage(undefined);

        return;
      }
      if (getFnsRegistryDateByInnResponse?.success && getFnsRegistryDateByInnResponse?.data) {
        setFnsRegistryCheckDateMessage(
          `Дата получения выписки ФНС ${convertInitialKnmDateToFormDate(
            getFnsRegistryDateByInnResponse.data,
            DATE_FORMAT_LONG_FNS,
          )}. ${FnsRegistryCheckMessageEnum.PLEASE_SAVE_KNM}`,
        );
      } else {
        setFnsRegistryCheckDateMessage(undefined);
      }
    },
    [getFnsRegistryDateInn, isMounted],
  );

  const fetchFnsLookup = useCallback(
    async (innValue: string) => {
      const getFnsLookupByInnResponse = await getFnsLookupByInn(innValue);

      if (!isMounted()) {
        return;
      }
      setLookupData && setLookupData(getFnsLookupByInnResponse.data);

      return getFnsLookupByInnResponse;
    },
    [getFnsLookupByInn, setLookupData, isMounted],
  );

  const updateAutoCompleteOptions = useCallback(
    async (value?: string) => {
      if (!value || value.length < 3) {
        setAutoCompleteOptions(undefined);
      } else if (value && (value.length === 10 || value.length === 12)) {
        if (!isStringDigitsOnly(value)) {
          return;
        }
        if (isShowAutoCompleteField === undefined || isShowAutoCompleteField) {
          const getFnsLookupByInnResponse = await fetchFnsLookup(value);

          if (getFnsLookupByInnResponse?.success && getFnsLookupByInnResponse.data) {
            const legalEntityLookup = getFnsLookupByInnResponse.data;
            const legalEntityLookupText = generateLegalEntityLookupText(legalEntityLookup);

            if (legalEntityLookupText) {
              setAutoCompleteOptions([
                {
                  label: legalEntityLookupText,
                  value,
                  content: legalEntityLookup,
                },
              ]);
            } else {
              setAutoCompleteOptions([]);
            }
          } else {
            setAutoCompleteOptions([]);
          }
        }
      }
    },
    [fetchFnsLookup, isShowAutoCompleteField],
  );

  const debouncedUpdateAutoCompleteOptions = debounce(updateAutoCompleteOptions, 2000);

  useEffect(() => {
    if (isFnsCheckFound && inn) {
      fetchFnsLookup(inn);
    }
  }, [isFnsCheckFound, inn, fetchFnsLookup]);

  const handleAutoCompleteChange = (value: string) => {
    setLookupData && setLookupData(true);
    onChangeInn && onChangeInn(value);
    debouncedUpdateAutoCompleteOptions(value);
    value &&
      isShowCheckResultMessage &&
      setCheckByInnMessage &&
      setCheckByInnMessage(FnsRegistryCheckMessageEnum.PLEASE_SAVE_KNM);
    if (onAutoCompleteChange) {
      onAutoCompleteChange(value);
    }
  };

  const handleAutoCompleteSelect = (value: string) => () => {
    debouncedUpdateAutoCompleteOptions();
    setAutoCompleteOptions(undefined);
    const currentOption = autoCompleteOptions?.find(item => item.value === value);
    const selectedLegalEntityLookup = currentOption?.content;

    onAutoCompleteSelect && selectedLegalEntityLookup && onAutoCompleteSelect(selectedLegalEntityLookup);
  };

  const handleInputBlur = (e: FocusEvent<HTMLInputElement>) => {
    const tagName = (e?.relatedTarget as Element)?.tagName;

    if (tagName !== 'UL') {
      debouncedUpdateAutoCompleteOptions();
      setAutoCompleteOptions(undefined);
    }
  };

  useEffect(() => {
    if (ervkId || !inn || !isShowCheckResultMessage) {
      setFnsCheckDateMessage(undefined);
      setFnsRegistryCheckDateMessage(undefined);

      return;
    }
    if (inn) {
      if (subjectType?.label === KnmSubjectTypesEnum.UR || subjectType?.code === 'UL') {
        if (checkSubjectResult?.egrul) {
          switch (checkSubjectResult?.egrul.status) {
            case FnsCheckStatusesEnum.FOUND: {
              setFnsCheckDateMessage(
                `Дата получения выписки ФНС ${convertInitialKnmDateToFormDate(
                  checkSubjectResult.egrul.time,
                  DATE_FORMAT_LONG_FNS,
                )}`,
              );
              setFnsRegistryErrorMessage(undefined);

              return;
            }
            case FnsCheckStatusesEnum.NO_DATA_53: {
              setFnsCheckDateMessage(undefined);
              setFnsRegistryErrorMessage(FnsRegistryCheckMessageEnum.NO_DATA_53_MESSAGE);

              return;
            }
            case FnsCheckStatusesEnum.NO_DATA_56: {
              setFnsCheckDateMessage(undefined);
              setFnsRegistryErrorMessage(FnsRegistryCheckMessageEnum.NO_DATA_56_MESSAGE);

              return;
            }
            case FnsCheckStatusesEnum.DATA_NOT_EQUALS_ERVK: {
              setFnsCheckDateMessage(undefined);
              setFnsRegistryErrorMessage(FnsRegistryCheckMessageEnum.DATA_NOT_EQUALS_ERVK_MESSAGE);

              return;
            }
            default: {
              fetchFnsRegistry(inn);
            }
          }
        } else {
          fetchFnsRegistry(inn);
        }
      } else if (subjectType?.label === KnmSubjectTypesEnum.IP || subjectType?.code === 'IP') {
        if (checkSubjectResult?.egrip) {
          switch (checkSubjectResult?.egrip.status) {
            case FnsCheckStatusesEnum.FOUND: {
              setFnsCheckDateMessage(
                `Дата получения выписки ФНС ${convertInitialKnmDateToFormDate(
                  checkSubjectResult.egrip.time,
                  DATE_FORMAT_LONG_FNS,
                )}`,
              );
              setFnsRegistryErrorMessage(undefined);

              return;
            }
            case FnsCheckStatusesEnum.NO_DATA_53: {
              setFnsCheckDateMessage(undefined);
              setFnsRegistryErrorMessage(FnsRegistryCheckMessageEnum.NO_DATA_53_MESSAGE);

              return;
            }
            case FnsCheckStatusesEnum.NO_DATA_56: {
              setFnsCheckDateMessage(undefined);
              setFnsRegistryErrorMessage(FnsRegistryCheckMessageEnum.NO_DATA_56_MESSAGE);

              return;
            }
            case FnsCheckStatusesEnum.DATA_NOT_EQUALS_ERVK: {
              setFnsCheckDateMessage(undefined);
              setFnsRegistryErrorMessage(FnsRegistryCheckMessageEnum.DATA_NOT_EQUALS_ERVK_MESSAGE);

              return;
            }
            default: {
              fetchFnsRegistry(inn);
            }
          }
        } else {
          fetchFnsRegistry(inn);
        }
      } else {
        setFnsCheckDateMessage(undefined);
        setFnsRegistryCheckDateMessage(undefined);
        setFnsRegistryErrorMessage(undefined);
      }
    } else {
      setFnsCheckDateMessage(undefined);
      setFnsRegistryCheckDateMessage(undefined);
      setFnsRegistryErrorMessage(undefined);
    }
  }, [ervkId, inn, subjectType, checkSubjectResult, fetchFnsRegistry]);

  const warningMessage = () => {
    if (fnsCheckDateMessage) {
      return undefined;
    }

    if (isShowAdditionalMessages && fnsRegistryCheckDateMessage) {
      return fnsRegistryCheckDateMessage;
    }

    return rest.warning;
  };

  return (
    <LegalEntityAutoCompleteField
      {...rest}
      options={autoCompleteOptions}
      onChange={!isFiz ? handleAutoCompleteChange : undefined}
      onSelect={!isFiz ? handleAutoCompleteSelect : undefined}
      onBlur={!isFiz ? handleInputBlur : undefined}
      success={isShowAdditionalMessages ? fnsCheckDateMessage : undefined}
      warning={warningMessage()}
      error={fnsRegistryErrorMessage}
    />
  );
};

const mapStateToProps = (state: IRootState) => ({
  created: getKnmDataCreated(state),
  settings: getAdminFetchSettings(state),
});

export const KnmInnField = connect(mapStateToProps)(KnmInnFieldWithoutProps);
